import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TagChip } from "@/components/TagChip";
import type { Tag } from "@/features/tags/types";
import { type CostItem, formatChf, plannedYearDisplay } from "./types";

/**
 * Sortable table of cost items. Clicking a row calls `onRowClick` so
 * the page-level drawer can open in edit mode. Sorting is client-side
 * (filtering is server-side in the data hook) since result sets here
 * are bounded by a single object's catalog.
 */
export interface CostItemListProps {
  items: CostItem[];
  /**
   * All tags defined for this object. Used to materialise tag chips per row
   * from each item's ``tag_ids`` list — the parent prefetches once and
   * passes the lookup down to avoid N+1 fetches.
   */
  tags?: Tag[];
  onRowClick: (item: CostItem) => void;
}

type SortKey =
  | "title"
  | "bkp_code"
  | "status"
  | "priority"
  | "planned_year"
  | "planned_amount_chf"
  | "actual_amount_chf";

interface SortState {
  key: SortKey;
  dir: "asc" | "desc";
}

function priorityRank(p: string): number {
  switch (p) {
    case "low":
      return 0;
    case "med":
      return 1;
    case "high":
      return 2;
    case "urgent":
      return 3;
    default:
      return -1;
  }
}

function compareItems(a: CostItem, b: CostItem, key: SortKey): number {
  switch (key) {
    case "planned_amount_chf":
      return Number(a.planned_amount_chf ?? "0") - Number(b.planned_amount_chf ?? "0");
    case "actual_amount_chf":
      return Number(a.actual_amount_chf ?? "0") - Number(b.actual_amount_chf ?? "0");
    case "planned_year": {
      const av = a.effective_planned_year ?? a.planned_year ?? -Infinity;
      const bv = b.effective_planned_year ?? b.planned_year ?? -Infinity;
      return av - bv;
    }
    case "priority":
      return priorityRank(a.priority) - priorityRank(b.priority);
    case "title":
      return a.title.localeCompare(b.title, "de");
    case "bkp_code":
      return (a.bkp_code ?? "").localeCompare(b.bkp_code ?? "", "de");
    case "status":
      return a.status.localeCompare(b.status, "de");
  }
}

export function CostItemList({
  items,
  tags = [],
  onRowClick,
}: CostItemListProps): JSX.Element {
  const { t } = useTranslation();
  const [sort, setSort] = useState<SortState>({
    key: "title",
    dir: "asc",
  });

  const tagById = useMemo(() => {
    const m = new Map<string, Tag>();
    for (const t of tags) m.set(t.id, t);
    return m;
  }, [tags]);

  const sorted = useMemo(() => {
    const copy = items.slice();
    copy.sort((a, b) => {
      const c = compareItems(a, b, sort.key);
      return sort.dir === "asc" ? c : -c;
    });
    return copy;
  }, [items, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" },
    );
  };

  const arrow = (key: SortKey) =>
    sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  if (items.length === 0) {
    return <p className="text-ink-muted">{t("costs.empty")}</p>;
  }

  const columns: Array<[SortKey, string]> = [
    ["title", t("costs.fields.title")],
    ["bkp_code", t("costs.fields.bkp")],
    ["status", t("costs.fields.status")],
    ["priority", t("costs.fields.priority")],
    ["planned_year", t("costs.fields.plannedYear")],
    ["planned_amount_chf", t("costs.fields.plannedAmount")],
    ["actual_amount_chf", t("costs.fields.actualAmount")],
  ];

  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-rule text-left">
          {columns.map(([key, label]) => (
            <th
              key={key}
              className="cursor-pointer px-3 py-3 text-[0.65rem] font-semibold uppercase tracking-[0.15em] text-ink-muted"
            >
              <button
                type="button"
                onClick={() => toggleSort(key)}
                className="hover:text-accent"
              >
                {label}
                {arrow(key)}
              </button>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((item) => (
          <tr
            key={item.id}
            onClick={() => onRowClick(item)}
            className="cursor-pointer border-b border-rule text-ink transition hover:bg-paper-sunk"
          >
            <td className="px-3 py-3 font-medium">
              <div className="flex flex-wrap items-center gap-1.5">
                <span>{item.title}</span>
                {item.tag_ids
                  ? item.tag_ids
                      .map((id) => tagById.get(id))
                      .filter((t): t is Tag => Boolean(t))
                      .map((tag) => <TagChip key={tag.id} tag={tag} />)
                  : null}
                {item.lot_ids && item.lot_ids.length > 0 && (
                  <span
                    className="inline-flex items-center rounded-sheet border border-rule px-1.5 py-0.5 text-[0.65rem] uppercase tracking-wider text-ink-muted"
                    title={t("costs.inLotsTitle")}
                  >
                    {t("costs.inLots", { count: item.lot_ids.length })}
                  </span>
                )}
              </div>
            </td>
            <td className="px-3 py-3 font-mono text-xs text-ink-muted">
              {item.bkp_code ?? (
                <span className="italic text-ink-subtle">
                  {t("costs.uncategorised")}
                </span>
              )}
            </td>
            <td className="px-3 py-3 text-ink-muted">
              {t(`costs.status.${item.status}`)}
            </td>
            <td className="px-3 py-3 text-ink-muted">
              {t(`costs.priority.${item.priority}`)}
            </td>
            <td className="px-3 py-3 font-mono tabular-nums text-ink-muted">
              {(() => {
                const { year, inherited } = plannedYearDisplay(item);
                if (year == null) return "—";
                return inherited ? (
                  <span
                    className="italic text-ink-subtle"
                    title={t("costs.fields.plannedYearInherited")}
                  >
                    {year}
                  </span>
                ) : (
                  year
                );
              })()}
            </td>
            <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
              {formatChf(item.planned_amount_chf)}
            </td>
            <td className="px-3 py-3 text-right font-mono tabular-nums text-ink">
              {formatChf(item.actual_amount_chf)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
